"use client";

import Link from "next/link";
import { type AnchorHTMLAttributes, type MouseEventHandler, type ReactNode } from "react";
import { sendGtmEvent } from "~/lib/gtm/gE";
import { GtmEvent } from "~/lib/gtm/getGtmEventData";
import { sendWebEvent } from "~/lib/webevents/WebEventsClient"
import { useUnauthorizedEventService } from "~/services/api/services/event";
import { isEmpty } from "~/utils/isEmpty";

export interface GTMEventLinkProps
    extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick" | "children"> {
    href: string;
    handleClick?: MouseEventHandler<HTMLAnchorElement>;
    children: ReactNode;
    gtmEventData?: GtmEvent;
    preventDefault?: boolean;
}

const GTMEventLink = ({
    href,
    handleClick,
    gtmEventData,
    children,
    preventDefault = false,
    ...props
}: GTMEventLinkProps) => {
    const isExternal = href.startsWith("http");
    const Component = isExternal ? "a" : Link;
    const sendBigQueryEvent = useUnauthorizedEventService();

    const handleOnClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
        if (preventDefault) event.preventDefault();

        if (gtmEventData && !isEmpty(gtmEventData)) {
            const url = isExternal && new URL(href);
            const isThirtpartyLink = isExternal && url && !url.hostname.includes("innerbalance.com");
            gtmEventData.value.href = isThirtpartyLink || !url ? href : url.pathname;

            sendGtmEvent(gtmEventData);

            sendWebEvent(sendBigQueryEvent, {
                event_name: gtmEventData.value.event_name,
                event_type: gtmEventData.event,
                widget: gtmEventData.value.widget,
                meta_data: gtmEventData.value.meta_data,
                href: isThirtpartyLink || !url ? href : url.pathname,
            });
        }
        if (handleClick && typeof handleClick === "function") {
            handleClick(event);
        }
    };

    return (
        <Component
            {...props}
            onClick={handleOnClick}
            href={href}
        >
            {children}
        </Component>
    );
};

export default GTMEventLink;
